import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { toast } from "sonner";
import { CalendarDays, Plus, Users, Pencil, Trash2, PlayCircle, Eye, EyeOff } from "lucide-react";

const TYPE_LABELS = { webinaire: "Webinaire", seminaire: "Événement", congres: "Congrès" };
const STATUT_LABELS = { BROUILLON: "Brouillon", PUBLIE: "Publié", ARCHIVE: "Archivé" };
const STATUT_CLS = {
  BROUILLON: "bg-cfhtb-warning/10 text-cfhtb-warning border-cfhtb-warning/30",
  PUBLIE: "bg-cfhtb-success/10 text-cfhtb-success border-cfhtb-success/30",
  ARCHIVE: "bg-slate-100 text-cfhtb-text border-slate-300",
};

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

export default function SeminarsList() {
  const navigate = useNavigate();
  const [seminaires, setSeminaires] = useState(null);

  const load = () => api("/admin/seminaires").then(setSeminaires).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const remove = async (s) => {
    if (!window.confirm(`Supprimer le événement « ${s.titre} » ?`)) return;
    try {
      await api(`/admin/seminaires/${s.id}`, { method: "DELETE" });
      toast.success("Événement supprimé");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const toggleMasque = async (s) => {
    try {
      await api(`/admin/seminaires/${s.id}/visibilite`, { method: "PATCH", body: { masque: !s.masque } });
      toast.success(!s.masque ? "Événement masqué de l'extranet" : "Événement de nouveau visible");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <AppLayout variant="admin">
      <div data-testid="admin-seminars-page" className="space-y-6 animate-fade-in">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <CalendarDays className="h-7 w-7 text-cfhtb-primary" />
            <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Événements</h1>
          </div>
          <button
            data-testid="seminar-new-button"
            onClick={() => navigate("/admin/seminaires/nouveau")}
            className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] transition-colors"
          >
            <Plus className="h-4 w-4" /> Nouveau événement
          </button>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="seminars-table">
              <thead className="bg-[#F7F7F7] border-b border-slate-200">
                <tr>
                  {["Titre", "Type", "Date", "Statut", "Inscrits", "Replay", ""].map((h, i) => (
                    <th key={h} className={`text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-3 ${i === 6 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!seminaires ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-cfhtb-text">Chargement…</td></tr>
                ) : seminaires.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-cfhtb-text" data-testid="seminars-empty">Aucun événement.</td></tr>
                ) : (
                  seminaires.map((s) => (
                    <tr key={s.id} className="border-b border-slate-100 hover:bg-cfhtb-bg/50 transition-colors" data-testid={`seminar-row-${s.id}`}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-cfhtb-heading">{s.titre}</p>
                        <p className="text-xs text-cfhtb-text/70">{(s.intervenants || []).join(", ")}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-cfhtb-text">{TYPE_LABELS[s.type] || s.type}</td>
                      <td className="px-4 py-3 text-sm text-cfhtb-text whitespace-nowrap">{formatDate(s.date_debut)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium border ${STATUT_CLS[s.statut] || ""}`}>
                          {STATUT_LABELS[s.statut] || s.statut}
                        </span>
                        {s.masque && (
                          <span data-testid={`seminar-masque-badge-${s.id}`} className="ml-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium border bg-cfhtb-error/10 text-cfhtb-error border-cfhtb-error/30">
                            <EyeOff className="h-3 w-3" /> Masqué
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-cfhtb-text">{s.nb_inscrits ?? 0}</td>
                      <td className="px-4 py-3">
                        {s.replay_disponible ? (
                          <PlayCircle className="h-4 w-4 text-cfhtb-success" />
                        ) : (
                          <span className="text-xs text-cfhtb-text/50">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1">
                          <button data-testid={`seminar-visibilite-${s.id}`} onClick={() => toggleMasque(s)}
                            className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-bg hover:text-cfhtb-primary transition-colors"
                            title={s.masque ? "Masqué de l'extranet — cliquer pour afficher" : "Visible — cliquer pour masquer"}>
                            {s.masque ? <EyeOff className="h-4 w-4 text-cfhtb-error" /> : <Eye className="h-4 w-4" />}
                          </button>
                          <Link to={`/admin/seminaires/${s.id}/inscrits`} data-testid={`seminar-enrollments-${s.id}`}
                            className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-bg hover:text-cfhtb-primary transition-colors" title="Inscrits">
                            <Users className="h-4 w-4" />
                          </Link>
                          <Link to={`/admin/seminaires/${s.id}/modifier`} data-testid={`seminar-edit-${s.id}`}
                            className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-bg hover:text-cfhtb-primary transition-colors" title="Modifier">
                            <Pencil className="h-4 w-4" />
                          </Link>
                          <button data-testid={`seminar-delete-${s.id}`} onClick={() => remove(s)}
                            className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-error/10 hover:text-cfhtb-error transition-colors" title="Supprimer">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
